import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from '@mui/material';

export type Column<T> = {
  header: string | React.ReactNode;
  accessor?: (row: T) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
};

type DataTableProps<T> = {
  title: string;
  data?: T[];
  isLoading: boolean;
  error?: unknown;
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  summaryLabel?: string;
};

export function DataTable<T>({
  title,
  data = [],
  isLoading,
  error,
  columns,
  onRowClick,
  summaryLabel,
}: DataTableProps<T>) {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#0D1117',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert
        severity="error"
        sx={{
          backgroundColor: '#FF4B4B',
          color: '#FFFFFF',
          maxWidth: '600px',
          margin: 'auto',
        }}
      >
        {String(error)}
      </Alert>
    );
  }

  const totalCount = data.length;

  return (
    <Box sx={{ p: 4, backgroundColor: 'rgba(14, 23, 32, 0)', minHeight: '100vh' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
        {title}
      </Typography>

      <Paper elevation={0} sx={{ backgroundColor: 'transparent', borderRadius: '5px' }}>
        <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#071019' }}>
              {columns.map((col, i) => (
                <TableCell
                  key={i}
                  align={col.align}
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 'bold',
                    borderTop: '1px solid #394047',
                    borderBottom: '1px solid #394047',
                    ...(i === 0 && {
                      borderTopLeftRadius: 5,
                      borderBottomLeftRadius: 5,
                      borderLeft: '1px solid #394047',
                    }),
                    ...(i === columns.length - 1 && {
                      borderTopRightRadius: 5,
                      borderBottomRightRadius: 5,
                      borderRight: '1px solid #394047',
                      pl: 2,
                    }),
                  }}
                >
                  {col.header}
                </TableCell>

              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{
                    color: '#A0AAB4',
                    py: 4,
                    borderBottom: 'none'
                  }}
                >
                  No sessions found.
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, ri) => (
                <TableRow
                  key={ri}
                  hover={!!onRowClick}
                  sx={{ cursor: onRowClick ? 'pointer' : undefined }}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((col, ci) => (
                    <TableCell
                      key={ci}
                      align={col.align}
                      sx={{ color: 'white', borderBottom: '1px solid #262E36' }}
                    >
                      {col.accessor ? col.accessor(row) : null}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}

            {summaryLabel && (
              <TableRow>
                <TableCell
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 'bold',
                    borderBottom: '1px solid #262E36',
                  }}
                >
                  {summaryLabel}
                </TableCell>
                <TableCell
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    borderBottom: '1px solid #262E36',
                  }}
                >
                  {totalCount}
                </TableCell>
                {columns.length > 2 && (
                  <TableCell sx={{ borderBottom: '1px solid #262E36' }} />
                )}
              </TableRow>
            )}
          </TableBody>

        </Table>
      </Paper>
    </Box>
  );
}
